import { withoutSpace } from '@/app/considerations/appliesToPublishCheck';
import AppIcon from '@/components/AppIcon';
import { SpaceDao } from '@rock/db-admin';
import { DraftBadge, PublishedBadge } from '@/components/badges/Common';
import { Button } from '@/components/ui/button';
import { Form, FormDescription } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { useAppliesToGuard } from '@/db/useAppliesToGuard';
import { useBusyAction } from '@/db/useBusyAction';
import { AppCheckboxField, AppInputField, AppSelectField, AppTextareaField } from '@/form/FormUI';
import { spaceState } from '@/state';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import logger from '@/util/logger';
import { zodResolver } from '@hookform/resolvers/zod';
import { type Space, SpaceGroupIdSchema, type SpaceId, SpaceSchema } from '@rock/types';
import { CircleX, PlusCircleIcon } from 'lucide-react';
import { type FC, useCallback, useState } from 'react';
import { type FieldErrors, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useAtomLoadable } from '../../state/loadableHooks';

const log = logger('SpaceDetails');
const spaceDao = new SpaceDao();

export default function SpaceDetails({ spaceId }: { spaceId: SpaceId }) {
    const spaceLoadable = useAtomLoadable(spaceState(spaceId));
    const [editing, setEditing] = useState(false);
    const guard = useAppliesToGuard();
    const { run } = useBusyAction(log, { spaceId });

    if (spaceLoadable.state === 'hasData' && spaceLoadable.data) {
        return (
            <div>
                {!editing ? (
                    <DisplayDetails space={spaceLoadable.data} startEditing={() => setEditing(true)} />
                ) : (
                    <EditDetails
                        space={spaceLoadable.data}
                        updateSpace={(updated) =>
                            run('Save space', async () => {
                                // Unpublishing takes the space's types out of the query catalog.
                                if (spaceLoadable.data?.published && !updated.published) {
                                    await guard(`Unpublishing ${spaceId}`, (catalog) => withoutSpace(catalog, spaceId));
                                }
                                // The form's own values, merged field-by-field: a whole-doc save would carry
                                // the space as it looked when the editor opened.
                                await spaceDao.updateSpace(spaceId, updated);
                                setEditing(false);

                                toast.success('Space Details Updated');
                                log.info(`Space: ${updated.id} updated successfully`);
                            })
                        }
                        onCancel={() => setEditing(false)}
                    />
                )}
            </div>
        );
    }
}

const DisplayDetails: FC<{ space: Space; startEditing: () => void }> = ({ space, startEditing }) => {
    return (
        <div className="space-y-2">
            <div className="flex flex-row items-center justify-between gap-2">
                {space.published ? <PublishedBadge /> : <DraftBadge />}
                <Button onClick={startEditing} variant="outline" size="sm" className="cursor-pointer">
                    Edit
                </Button>
            </div>
            <div className="space-y-4">
                <div>
                    <Label>Name</Label>
                    <div className="text-gray-500">{space.name}</div>
                </div>
                <div>
                    <Label>Group</Label>
                    <div className="text-gray-500">{space.groupId}</div>
                </div>
                <div>
                    <Label>System Title</Label>
                    <div className="text-gray-500">{space.systemTitle}</div>
                </div>
                <div>
                    <Label>Icon</Label>
                    <div className="text-gray-500">{space.icon && <AppIcon icon={space.icon} size={30} />}</div>
                </div>
                <div>
                    <Label>Intro</Label>
                    <div className="text-gray-500">
                        {space.intro?.map((intro, i) => (
                            <p key={i}>{intro.text}</p>
                        ))}
                    </div>
                </div>
                <div>
                    <Label>Add Equipment Label</Label>
                    <div className="text-gray-500">{space.addEquipmentLabel || '—'}</div>
                </div>
                <div>
                    <Label>Contacts Title</Label>
                    <div className="text-gray-500">{space.contactsTitle || '—'}</div>
                </div>
                <div>
                    <Label>Contacts Empty Text</Label>
                    <div className="text-gray-500 whitespace-pre-wrap">{space.contactsEmptyText || '—'}</div>
                </div>
            </div>
        </div>
    );
};

const EditDetails: FC<{
    space: Space;
    updateSpace: (space: Space) => void;
    onCancel: () => void;
}> = ({ space, updateSpace, onCancel }) => {
    const form = useForm({
        resolver: zodResolver(SpaceSchema),
        defaultValues: space,
    });

    const intros = useFieldArray({
        control: form.control,
        name: 'intro',
    });

    const onSubmit = useCallback(
        (space: Space) => {
            updateSpace(space);
        },
        [updateSpace],
    );

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const handleCancel = useCallback(() => {
        onCancel();
    }, [onCancel]);

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="flex flex-col gap-8 items-stretch">
                <AppCheckboxField label="Published" control={form.control} name="published" />
                <AppInputField
                    label="Name"
                    placeholder="Name"
                    control={form.control}
                    name="name"
                    description="The display name of the space"
                />
                <AppSelectField
                    label="Group"
                    placeholder="Group"
                    control={form.control}
                    name="groupId"
                    description="The group to show this space in"
                    options={SpaceGroupIdSchema.options}
                />
                <AppInputField
                    label="System Title"
                    placeholder="System Title"
                    control={form.control}
                    name="systemTitle"
                    description="The term we use to refer to the system as a whole."
                />
                <AppInputField
                    label="Icon"
                    placeholder="icon-name"
                    control={form.control}
                    name="icon"
                    description="Searchable list of icons available here: https://lucide.dev/icons (defaults to 'house' in-app if entered incorrectly)"
                />

                <AppInputField
                    label="Add Equipment Label"
                    placeholder='e.g. "Add Another Surface"'
                    control={form.control}
                    name="addEquipmentLabel"
                    description='Optional override for the "Add Equipment" button label shown in-app.'
                />

                <AppInputField
                    label="Contacts Title"
                    placeholder='e.g. "Service Providers"'
                    control={form.control}
                    name="contactsTitle"
                    description='Optional override for the "Contacts" section title shown above the contact list in-app.'
                />

                <AppTextareaField
                    label="Contacts Empty Text"
                    placeholder="e.g. Add your HVAC tech, plumber, or any business you want to remember."
                    control={form.control}
                    name="contactsEmptyText"
                    description="Optional override for the description shown when the contacts list is empty."
                />

                <div className="flex flex-col gap-2 items-stretch group/intros">
                    <Label>Intro</Label>
                    <FormDescription>The introduction paragraphs before the guide starts</FormDescription>
                    {intros.fields.map((introField, introIndex) => (
                        <div key={introField.id} className="flex flex-row items-start gap-3 group/intro">
                            {/* Same growing box the node forms' prompts use — intro paragraphs are
                                sentences, not labels, and a one-line input hides most of one. */}
                            <AppTextareaField
                                control={form.control}
                                name={`intro.${introIndex}.text`}
                                placeholder="Enter the text"
                                className="flex-1"
                                rows={1}
                            />
                            <CircleX
                                className="mt-2 cursor-pointer opacity-0 max-md:opacity-100 group-hover/intro:opacity-100 transition-opacity fill-white hover:fill-red-300"
                                onClick={() => intros.remove(introIndex)}
                            />
                        </div>
                    ))}
                    <div className="text-center">
                        <Button
                            type="button"
                            onClick={() => intros.append({ text: '' })}
                            variant="ghost"
                            size="sm"
                            className="cursor-pointer h-6 text-xs opacity-0 max-md:opacity-100 group-hover/intros:opacity-100 transition-opacity"
                        >
                            <PlusCircleIcon /> Add Paragraph
                        </Button>
                    </div>
                </div>

                <div className="flex flex-row gap-3">
                    <Button type="submit" className="cursor-pointer">
                        Save Details
                    </Button>
                    <Button type="button" className="cursor-pointer" variant="outline" onClick={handleCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};
